import { inject, Injectable, Service } from '@angular/core';
import { defer, Observable, of } from 'rxjs';
import { concatMap, map, tap } from 'rxjs/operators';

import { RolesEnum } from '../enums/roles.enum';
import { Shop } from '../models/shop.model';
import { UserInfo } from '../models/user-info.model';
import { LoaderService } from '../services/loader.service';
import { UtilityFunctions } from '../utilities/functions.models';
import { ContextApiService } from './context-api.service';
import { NavigationStore } from './navigation.store';
import { SessionStore } from './session.store';
import { ShopContextStore } from './shop-context.store';

/**
 * Carica il contesto dell'app: utente e store disponibili.
 * La cache sono gli store, non una copia privata (ADR 005).
 * Observable freddi: chi chiama DEVE sottoscriversi, altrimenti non parte niente.
 */
@Service()
export class ContextInitService {
  private readonly _api = inject(ContextApiService);
  private readonly _loader = inject(LoaderService);
  private readonly _session = inject(SessionStore);
  private readonly _shopContext = inject(ShopContextStore);
  private readonly _navigation = inject(NavigationStore);

  /** Utente corrente: fetch solo se `SessionStore` è vuoto. */
  userInfo(loaderId?: string): Observable<UserInfo> {
    return defer(() => {
      const cached = this._session.userInfo();
      if (cached) return of(cached);

      return this._api.fetchUserInfo(loaderId).pipe(
        tap(userInfo => {
          this._session.setUserInfo(userInfo);
          if (userInfo.mainRole <= RolesEnum.Customer) this._navigation.startRetailModeLocalStorageSync();
          if (userInfo.mainRole === RolesEnum.CustomerGuest) this.setIsUserGuest(true);
        })
      );
    });
  }

  /** Store disponibili: fetch solo se lo store è vuoto. Lista vuota = si rifetcha. */
  shops(): Observable<Shop[]> {
    return defer(() => {
      const cached = this._shopContext.shops();
      if (cached?.length) return of(cached);

      return this._api.fetchShops().pipe(
        tap(shops => this._shopContext.setShops(shops))
      );
    });
  }

  /**
   * Utente + store, in sequenza (`concatMap`: parità con l'ordine storico).
   * Debito noto: senza `finalize`, un errore HTTP lascia il loader aperto.
   */
  context(loaderId?: string): Observable<{ UserInfo: UserInfo, Stores: Shop[] }> {
    return defer(() => {
      if (loaderId) this._loader.showLoader(loaderId);

      return this.userInfo().pipe(
        concatMap(UserInfo => this.shops().pipe(map(Stores => ({ UserInfo, Stores })))),
        tap(() => {
          if (loaderId) this._loader.dismissLoader(loaderId);
        })
      );
    });
  }

  /** Imposta lo shop della URL + i suoi canali. La vetrina NON lo usa: lì i canali non si toccano. */
  applyCurrentShopFromUrl(shops: Shop[]): void {
    if (!shops?.length) return;

    const shop = UtilityFunctions.getCurrentShopByUrl(shops);
    if (shop) {
      this._shopContext.setCurrentShop(shop);
      this._shopContext.setChannels(shop.activatedStoreChannels);
    }
  }

  /** Ospite: due store da aggiornare insieme. */
  private setIsUserGuest(value: boolean): void {
    this._navigation.setOnlyRetailMode(value, { skipLocalStorageUpdate: true });
    this._session.setIsUserGuest(value);
  }
}
